import DOMPurify from 'dompurify';
import FeatureLockScreen from '../../components/apple-ui/FeatureLockScreen';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import ReferralToast from '../../components/apple-ui/ReferralToast';
import { shouldShowExportReferral, markExportReferralShown, shouldShowFirstFeatureReferral, markFirstFeatureUsed, EXPORT_MSG, FIRST_FEATURE_MSG } from '../../utils/referralTriggers';
import React, { useState, useEffect, useRef } from 'react';
import MainLayoutApple from '@/components/apple-ui/MainLayoutApple';
import GlobalContextBarApple from '@/components/apple-ui/GlobalContextBarApple';
import ContextSelectorModal from '@/components/ContextSelectorModal';
import ActiveContextBadgesApple from '@/components/apple-ui/ActiveContextBadgesApple';
import CostEstimateApple from '@/components/apple-ui/CostEstimateApple';
import { usePlan } from '@/contexts/PlanContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useGlobalContext } from '@/contexts/GlobalContextProvider';
import { 
  FileUp, 
  FileText, 
  Download, 
  Loader2, 
  Lock, 
  CheckCircle,
  AlertTriangle, 
  ArrowRight, 
  ArrowLeft,
  FileCheck, 
  History, 
  X,
  Check,
  XCircle,
  MessageSquare,
  Sparkles,
  ChevronDown,
  ChevronRight,
  Edit3,
  Save,
  Trash2,
  Eye,
  List,
  LayoutList,
  FolderOpen,
  PenTool,
  Plus,
  Calculator
} from 'lucide-react';
import { toast } from 'sonner';
import ExportFeedbackModal from '../../components/ExportFeedbackModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUPPORTED_FILE_TYPES = ['.docx', '.pdf', '.xlsx', '.txt'];
const FILE_ACCEPT_STRING = SUPPORTED_FILE_TYPES.join(',');

const LANGUAGES = [
  'English', 'Hindi', 'Punjabi', 'Gujarati', 'Marathi',
  'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Bengali',
  'Odia', 'Assamese', 'Urdu'
];

const DEFAULT_CHECKS = {
  grammar: true,
  spelling: true,
  punctuation: true,
  style: true,
  consistency: true,
  tone: false
};

// Issue type colors
const ISSUE_COLORS = {
  grammar: { bg: 'rgba(255, 59, 48, 0.15)', border: '#FF3B30', text: '#FF3B30' },
  spelling: { bg: 'rgba(255, 59, 48, 0.15)', border: '#FF3B30', text: '#FF3B30' },
  punctuation: { bg: 'rgba(255, 149, 0, 0.15)', border: '#FF9500', text: '#FF9500' },
  style: { bg: 'rgba(255, 149, 0, 0.15)', border: '#FF9500', text: '#FF9500' },
  consistency: { bg: 'rgba(0, 122, 255, 0.15)', border: '#007AFF', text: '#007AFF' },
  clarity: { bg: 'rgba(0, 122, 255, 0.15)', border: '#007AFF', text: '#007AFF' },
  tone: { bg: 'rgba(175, 82, 222, 0.15)', border: '#AF52DE', text: '#AF52DE' }
};

const ReviewPageApple = ({ user, planInfo, onLogout }) => {
  // Theme and Global Context hooks
  const { isDarkMode } = useTheme();
  const { showContextModal, setShowContextModal, getContextIds } = useGlobalContext();
  const { refreshPlanInfo } = usePlan();

  const reviewAccess = useFeatureAccess('review');

  // Mode selection: 'select' | 'proofread' | 'translation-qa'
  const [reviewMode, setReviewMode] = useState('select');
  const [showExportFeedback, setShowExportFeedback] = useState(false);
  const [referralToast, setReferralToast] = useState(null);

  const triggerExportReferral = () => {
    if (shouldShowExportReferral()) {
      markExportReferralShown();
      setReferralToast({ message: EXPORT_MSG, buttons: 'dismiss' });
    }
  };
  const triggerFirstFeatureReferral = () => {
    if (shouldShowFirstFeatureReferral()) {
      markFirstFeatureUsed();
      setTimeout(() => setReferralToast(prev => prev || { message: FIRST_FEATURE_MSG, buttons: 'remind_later' }), 3000);
    }
  };

  const exportFeedbackFeature = reviewMode === 'proofread' ? 'proofread'
    : reviewMode === 'super-review' ? 'smart_review'
    : 'tqa';
  const [srSourceText, setSrSourceText] = useState('');
  const [srTranslatedText, setSrTranslatedText] = useState('');
  const [srReviewerComments, setSrReviewerComments] = useState('');
  const [srSourceLang, setSrSourceLang] = useState('English');
  const [srTargetLang, setSrTargetLang] = useState('Hindi');
  const [srGlossary, setSrGlossary] = useState('');
  const [srLoading, setSrLoading] = useState(false);
  const [srCommentTicker, setSrCommentTicker] = useState({ processed: 0, total: 0, accepted: 0, rejected: 0 });
  const [srResults, setSrResults] = useState(null);
  const [srActiveTab, setSrActiveTab] = useState('clean');
  const [srHasBothFiles, setSrHasBothFiles] = useState(null);
  const [srSourceFile, setSrSourceFile] = useState(null);
  const [srTargetFile, setSrTargetFile] = useState(null);
  const [srTranslatedFile, setSrTranslatedFile] = useState(null);
  const [srWordCount, setSrWordCount] = useState(0);
  const [srReviewerName, setSrReviewerName] = useState('');
  const [srInputMode, setSrInputMode] = useState('text');
  
  // View state
  const [view, setView] = useState('upload');
  const [currentReviewId, setCurrentReviewId] = useState(null);
  
  // Upload state
  const [file, setFile] = useState(null);
  const [targetLang, setTargetLang] = useState('English');
  const [checks, setChecks] = useState(DEFAULT_CHECKS);
  const [uploading, setUploading] = useState(false);
  const [proofQuality, setProofQuality] = useState(0);
  const [proofreadStyleNotes, setProofreadStyleNotes] = useState('');
  const [tqaScores, setTqaScores] = useState({ accuracy: null, terminology: null, grammar: null, style: null });
  
  // NOTE: Project & Instructions are now handled by GlobalContextBar
  // Keeping minimal state for backward compatibility
  
  // Review state
  const [review, setReview] = useState(null);
  const [segments, setSegments] = useState([]);
  const [activeSegmentId, setActiveSegmentId] = useState(null);
  const [loading, setLoading] = useState(false);

  const [reviewingSegment, setReviewingSegment] = useState(null);
  const [reviewingAll, setReviewingAll] = useState(false);
  
  // Manual review state
  const [manualEditMode, setManualEditMode] = useState(false);
  const [manualEditText, setManualEditText] = useState('');
  const [manualComment, setManualComment] = useState('');
  const [savingManualReview, setSavingManualReview] = useState(false);
  
  // History state
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Export state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportType, setExportType] = useState('clean');
  const [exporting, setExporting] = useState(false);
  
  // Translation QA state
  const [sourceFile, setSourceFile] = useState(null);
  const [targetFile, setTargetFile] = useState(null);
  const [sourceLang, setSourceLang] = useState('English');
  const [tqaChecks, setTqaChecks] = useState({
    accuracy: true,
    completeness: true,
    terminology: true,
    instructionCompliance: true,
    grammar: true,
    style: false,
    cultural: false
  });
  
  // Word count state for cost estimation
  // Word count state for cost estimation
  // -1 means "calculating..." (file selected but not yet uploaded)
  // 0 means no file selected
  // >0 means accurate count from API
  const [sourceWordCount, setSourceWordCount] = useState(0);
  const [targetWordCount, setTargetWordCount] = useState(0);
  
  const fileInputRef = useRef(null);
  const sourceFileInputRef = useRef(null);
  const targetFileInputRef = useRef(null);

  // Reset word counts when files change (accurate counts come from API after upload)
  useEffect(() => {
    if (reviewMode === 'proofread') {
      setSourceWordCount(file ? -1 : 0);
    } else if (reviewMode === 'translation-qa' || reviewMode === 'tqa') {
      setSourceWordCount(sourceFile ? -1 : 0);
      setTargetWordCount(targetFile ? -1 : 0);
    }
  }, [file, sourceFile, targetFile, reviewMode]);

  // Simulate super-review comment ticker during loading
  useEffect(() => {
    if (!srLoading) { setSrCommentTicker({ processed: 0, total: 0, accepted: 0, rejected: 0 }); return; }
    const total = 8 + Math.floor(Math.random() * 6);
    setSrCommentTicker({ processed: 0, total, accepted: 0, rejected: 0 });
    let processed = 0, accepted = 0, rejected = 0;
    const iv = setInterval(() => {
      if (processed < total) {
        processed++;
        if (Math.random() > 0.25) accepted++; else rejected++;
        setSrCommentTicker({ processed, total, accepted, rejected });
      }
    }, 900);
    return () => clearInterval(iv);
  }, [srLoading]);

  // Simulate proofread quality meter during upload
  useEffect(() => {
    if (!uploading || reviewMode !== 'proofread') { setProofQuality(0); return; }
    setProofQuality(0);
    let q = 0;
    const iv = setInterval(() => {
      q = Math.min(q + 3 + Math.floor(Math.random() * 5), 95);
      setProofQuality(q);
    }, 400);
    return () => clearInterval(iv);
  }, [uploading, reviewMode]);

  // Simulate TQA score cards filling during translation-qa upload/review
  useEffect(() => {
    const isActive = (uploading || reviewingAll) && reviewMode === 'translation-qa';
    if (!isActive) { setTqaScores({ accuracy: null, terminology: null, grammar: null, style: null }); return; }
    const cats = ['accuracy', 'terminology', 'grammar', 'style'];
    let i = 0;
    const iv = setInterval(() => {
      if (i < cats.length) { setTqaScores(prev => ({ ...prev, [cats[i]]: 72 + Math.floor(Math.random() * 22) })); i++; }
      else { clearInterval(iv); }
    }, 1800);
    return () => clearInterval(iv);
  }, [uploading, reviewingAll, reviewMode]);

  // Feature gating — must be after all hooks
  if (!reviewAccess.hasAccess) {
    return <FeatureLockScreen feature="review" requiredPlan={reviewAccess.upgradeTo || 'professional'} currentPlan={reviewAccess.currentPlan} />;
  }

  // NOTE: Projects and context are now loaded from GlobalContextBar
  // Removed local loadProjects useEffect and functions

  // Load history
  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/review/history/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      setHistory(data.reviews || []);
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Load review
  const loadReview = async (reviewId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/review/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { toast.error('Failed to load review'); return; }
      const data = await res.json();

      setReview(data.review);
      setSegments(data.segments || []);
      
      // Update word counts from actual review data
      if (data.review) {
        if (data.review.source_word_count) {
          setSourceWordCount(data.review.source_word_count);
        }
        if (data.review.total_words) {
          setTargetWordCount(data.review.total_words);
        }
      }
      
      if (data.segments && data.segments.length > 0) {
        setActiveSegmentId(data.segments[0].segment_id);
      }
      
      setCurrentReviewId(reviewId);
      setView('review');
    } catch (error) {
      console.error('Failed to load review:', error);
      toast.error('Failed to load review');
    } finally {
      setLoading(false);
    }
  };

  // File handling
  const isFileSupported = (filename) => {
    const ext = '.' + filename.toLowerCase().split('.').pop();
    return SUPPORTED_FILE_TYPES.includes(ext);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!isFileSupported(selectedFile.name)) {
        toast.error(`Please upload a supported file format: ${SUPPORTED_FILE_TYPES.join(', ')}`);
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer?.files?.[0];
    if (droppedFile) {
      if (!isFileSupported(droppedFile.name)) {
        toast.error(`Please upload a supported file format: ${SUPPORTED_FILE_TYPES.join(', ')}`);
        return;
      }
      setFile(droppedFile);
    }
  };

  // Upload and start review
  const handleStartReview = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }
    
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('target_language', targetLang);
      formData.append('checks', JSON.stringify(checks));
      
      // Use global context for project and instruction
      // For proofread mode, use style notes instead of context IDs
      if (proofreadStyleNotes.trim()) {
        formData.append('project_instructions', proofreadStyleNotes.trim());
      }

      const res = await fetch(`${API_URL}/api/review/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      const data = await res.json();
      
      if (res.ok) {
        // Update word counts from API response (accurate count)
        if (data.total_words) {
          setSourceWordCount(data.total_words);
        }
        // Show cost estimate in success message
        const costInfo = data.cost_estimate ? (planInfo?.plan === 'payg' ? ` · ≈ ₹${(data.cost_estimate.words_to_deduct * 0.17).toFixed(2)}` : ` · ${data.cost_estimate.words_to_deduct} words`) : '';
        const contextInfo = data.context_applied?.instruction_name ? ` (Using: ${data.context_applied.instruction_name})` : '';
        toast.success((data.message || 'Document uploaded successfully') + costInfo + contextInfo);
        setTimeout(() => refreshPlanInfo(), 1500);
        loadReview(data.review_id);
      } else {
        toast.error(data.detail || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  // Start Translation QA
  const handleStartTranslationQA = async () => {
    if (!sourceFile || !targetFile) {
      toast.error('Please select both source and target files');
      return;
    }
    
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('source_file', sourceFile);
      formData.append('target_file', targetFile);
      formData.append('source_language', sourceLang);
      formData.append('target_language', targetLang);
      formData.append('mode', 'translation-qa');
      formData.append('checks', JSON.stringify(Object.keys(tqaChecks).filter(k => tqaChecks[k])));
      
      // Use global context for project and instruction
      const contextIds = getContextIds();
      if (contextIds.projectId) {
        formData.append('project_id', contextIds.projectId);
      }
      if (contextIds.instructionId) {
        formData.append('instruction_id', contextIds.instructionId);
      }
      if (contextIds.glossaryId) {
        formData.append('glossary_id', contextIds.glossaryId);
      }
      
      const res = await fetch(`${API_URL}/api/review/upload-tqa`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      const data = await res.json();
      
      if (res.ok) {
        // Update word counts from API response (accurate count)
        if (data.source_word_count) {
          setSourceWordCount(data.source_word_count);
        }
        if (data.total_words) {
          setTargetWordCount(data.total_words);
        }
        // Show cost estimate in success message
        const costInfo = data.cost_estimate ? (planInfo?.plan === 'payg' ? ` · ≈ ₹${(data.cost_estimate.words_to_deduct * 0.17).toFixed(2)}` : ` · ${data.cost_estimate.words_to_deduct} words`) : '';
        const contextInfo = data.context_applied?.instruction_name ? ` (Using: ${data.context_applied.instruction_name})` : '';
        toast.success((data.message || 'Documents uploaded successfully') + costInfo + contextInfo);
        setTimeout(() => refreshPlanInfo(), 1500);
        loadReview(data.review_id);
      } else {
        toast.error(data.detail || 'Upload failed');
      }
    } catch (error) {
      console.error('Translation QA upload error:', error);
      toast.error('Failed to upload documents');
    } finally {
      setUploading(false);
    }
  };

  // Auto-review single segment
  const handleAutoReviewSegment = async (segmentId) => {
    setReviewingSegment(segmentId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/review/auto-review-segment`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({
          review_id: currentReviewId,
          segment_id: segmentId
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setSegments(prev => prev.map(s => 
          s.segment_id === segmentId 
            ? { ...s, reviewed_text: data.reviewed_text, changes: data.changes, status: 'reviewed' }
            : s
        ));
        toast.success(`Reviewed with ${data.change_count} suggested changes`);
        triggerFirstFeatureReferral();
      } else {
        toast.error(data.detail || 'Review failed');
      }
    } catch (error) {
      console.error('Auto-review error:', error);
      toast.error('Failed to review segment');
    } finally {
      setReviewingSegment(null);
    }
  };

  // Auto-review all segments
  const handleAutoReviewAll = async () => {
    setReviewingAll(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/review/auto-review-all`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ review_id: currentReviewId })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(data.message);
        loadReview(currentReviewId);
      } else {
        toast.error(data.detail || 'Review failed');
      }
    } catch (error) {
      console.error('Auto-review all error:', error);
      toast.error('Failed to review all segments');
    } finally {
      setReviewingAll(false);
    }
  };

  // Accept changes
  const handleAcceptChanges = async (changeIds) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/review/accept-changes`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ review_id: currentReviewId, change_ids: changeIds })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(`Accepted ${data.accepted_count} changes`);
        loadReview(currentReviewId);
      } else {
        toast.error(data.detail || 'Failed to accept');
      }
    } catch (error) {
      toast.error('Failed to accept changes');
    }
  };

  // Reject changes
  const handleRejectChanges = async (changeIds) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/review/reject-changes`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ review_id: currentReviewId, change_ids: changeIds })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(`Rejected ${data.rejected_count} changes`);
        loadReview(currentReviewId);
      } else {
        toast.error(data.detail || 'Failed to reject');
      }
    } catch (error) {
      toast.error('Failed to reject changes');
    }
  };

  // Accept all
  const handleAcceptAll = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/review/accept-all`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ review_id: currentReviewId })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(data.message);
        loadReview(currentReviewId);
      } else {
        toast.error(data.detail || 'Failed');
      }
    } catch (error) {
      toast.error('Failed to accept all');
    }
  };

  // Reject all
  const handleRejectAll = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/review/reject-all`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ review_id: currentReviewId })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(data.message);
        loadReview(currentReviewId);
      } else {
        toast.error(data.detail || 'Failed');
      }
    } catch (error) {
      toast.error('Failed to reject all');
    }
  };

  // Export
  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/review/export`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ review_id: currentReviewId, export_type: exportType })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        let blob;
        
        // Handle binary formats (DOCX, XLSX, PDF)
        if (data.content_base64) {
          // Decode base64 to binary
          const binaryString = atob(data.content_base64);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          blob = new Blob([bytes], { type: data.mime_type || 'application/octet-stream' });
        } else {
          // Text content
          blob = new Blob([data.content], { type: data.mime_type || 'text/plain;charset=utf-8' });
        }
        
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.file_name;
        a.click();
        URL.revokeObjectURL(url);
        
        const formatLabel = data.format ? data.format.toUpperCase() : 'document';
        toast.success(`Exported ${formatLabel} successfully`);
        setShowExportModal(false);
        setShowExportFeedback(true);
        triggerExportReferral();
      } else {
        toast.error(data.detail || 'Export failed');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export');
    } finally {
      setExporting(false);
    }
  };

  // Manual review - start editing
  const startManualEdit = () => {
    if (activeSegment) {
      setManualEditText(activeSegment.reviewed_text || activeSegment.original_text);
      setManualComment('');
      setManualEditMode(true);
    }
  };

  // Manual review - save
  const handleSaveManualReview = async () => {
    if (!activeSegment || !manualEditText.trim()) {
      toast.error('Please enter reviewed text');
      return;
    }
    
    setSavingManualReview(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/review/manual-review-segment`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({
          review_id: currentReviewId,
          segment_id: activeSegment.segment_id,
          reviewed_text: manualEditText,
          comment: manualComment || 'Manual review by user'
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setSegments(prev => prev.map(s => 
          s.segment_id === activeSegment.segment_id 
            ? { ...s, reviewed_text: manualEditText, changes: data.changes || [], status: 'reviewed' }
            : s
        ));
        setManualEditMode(false);
        setManualEditText('');
        setManualComment('');
        toast.success('Manual review saved');
      } else {
        toast.error(data.detail || 'Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save manual review');
    } finally {
      setSavingManualReview(false);
    }
  };

  // Add manual comment to segment
  const handleAddManualComment = async (segmentId, commentText) => {
    if (!commentText.trim()) {
      toast.error('Please enter a comment');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/review/add-comment`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({
          review_id: currentReviewId,
          segment_id: segmentId,
          comment: commentText,
          comment_type: 'manual'
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success('Comment added');
        loadReview(currentReviewId);
      } else {
        toast.error(data.detail || 'Failed to add comment');
      }
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const activeSegment = segments.find(s => s.segment_id === activeSegmentId);
  
  const totalIssues = segments.reduce((sum, s) => sum + (s.changes?.length || s.issues?.length || 0), 0);
  const reviewedCount = segments.filter(s => s.status === 'reviewed' || s.status === 'accepted').length;
  const pendingCount = segments.filter(s => s.status === 'pending').length;
  const acceptedChanges = segments.reduce((sum, s) => 
    sum + (s.changes?.filter(c => c.status === 'accepted').length || 0), 0
  );

  // Render text with highlights
  const renderTextWithHighlights = (text, issues) => {
    if (!issues || issues.length === 0) return text;
    
    let result = [];
    let lastIndex = 0;
    
    const sortedIssues = [...issues]
      .filter(i => i.position)
      .sort((a, b) => a.position.start - b.position.start);
    
    sortedIssues.forEach((issue, idx) => {
      if (issue.position.start > lastIndex) {
        result.push(text.substring(lastIndex, issue.position.start));
      }
      
      const colors = ISSUE_COLORS[issue.type] || ISSUE_COLORS.grammar;
      
      result.push(
        <mark 
          key={idx}
          style={{
            backgroundColor: colors.bg,
            borderBottom: `2px solid ${colors.border}`,
            padding: '2px 4px',
            borderRadius: '4px'
          }}
          title={issue.description}
        >
          {text.substring(issue.position.start, issue.position.end)}
        </mark>
      );
      
      lastIndex = issue.position.end;
    });
    
    if (lastIndex < text.length) {
      result.push(text.substring(lastIndex));
    }
    
    return result;
  };

  const handleSuperReview = async () => {
    if (!srTranslatedFile && !srTranslatedText.trim()) { alert('Please provide the translated document.'); return; }
    if (srHasBothFiles && !srSourceFile && !srSourceText.trim()) { alert('Please provide the source document.'); return; }
    setSrLoading(true);
    setSrResults(null);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('source_language', srSourceLang);
      formData.append('target_language', srTargetLang);
      formData.append('reviewer_comments', srReviewerComments || '');  // comments extracted from DOCX
      formData.append('glossary', srGlossary || '');
      formData.append('has_source', srHasBothFiles ? 'true' : 'false');
      formData.append('reviewer_name', srReviewerName || 'Localization Lead');

      // Translated file — use uploaded file or create from pasted text
      if (srTranslatedFile) {
        formData.append('translated_file', srTranslatedFile);
      } else {
        const blob = new Blob([srTranslatedText], { type: 'text/plain' });
        formData.append('translated_file', blob, 'translated.txt');
      }

      // Source file if available
      if (srHasBothFiles) {
        if (srSourceFile) {
          formData.append('source_file', srSourceFile);
        } else if (srSourceText.trim()) {
          const blob = new Blob([srSourceText], { type: 'text/plain' });
          formData.append('source_file', blob, 'source.txt');
        }
      }

      const response = await fetch(`${API_URL}/api/review/super-review`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Super Review failed');
      }

      const data = await response.json();
      setSrResults(data);
      setView('results');
    } catch (e) {
      console.error('Super Review error:', e);
      alert('Super Review failed: ' + e.message);
    } finally {
      setSrLoading(false);
      setTimeout(() => refreshPlanInfo && refreshPlanInfo(), 1500);
    }
  };

  const downloadSrDocx = (b64data, filename) => {
    const bytes = atob(b64data);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    const blob = new Blob([arr], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const handleSrFileRead = (file, setter) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => setter(e.target.result);
    reader.readAsText(file);
  };

  return (
    <MainLayoutApple user={user} planInfo={planInfo} onLogout={onLogout}>
      {/* Shared shimmer keyframes for review overlays */}
      <style>{`@keyframes review-shimmer { 0% { transform: translateX(-200%); } 100% { transform: translateX(400%); } } @keyframes review-overlay-in { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }`}</style>
      {/* Proofreading overlay */}
      {(uploading || reviewingAll) && reviewMode === 'proofread' && (() => {
        const proofStageIdx = proofQuality < 25 ? 0 : proofQuality < 50 ? 1 : proofQuality < 75 ? 2 : 3;
        const proofStages = [
          { label: 'Uploading Document', Icon: FileUp },
          { label: 'Analysing Content', Icon: FileText },
          { label: 'Checking Quality', Icon: CheckCircle },
          { label: 'Report Ready', Icon: Sparkles },
        ];
        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', backgroundColor: isDarkMode ? 'rgba(10,15,26,0.92)' : 'rgba(255,255,255,0.95)' }}>
            <div style={{ background: isDarkMode ? '#111827' : '#ffffff', borderRadius: 16, padding: '40px 48px', width: '100%', maxWidth: 520, boxShadow: isDarkMode ? '0 20px 60px rgba(0,0,0,0.5)' : '0 20px 60px rgba(0,0,0,0.12)', border: isDarkMode ? '1px solid #1e293b' : '1px solid #e5e7eb', animation: 'review-overlay-in 0.2s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 44, height: 44, background: '#000', borderRadius: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: isDarkMode ? '0 0 0 2px #ffffff' : '0 0 0 1px rgba(0,86,255,.30), 0 4px 20px rgba(0,86,255,.22)' }}>
                    <img src="/logo-marketing.png" alt="t09n.com" style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 0, display: 'block' }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    <span style={{ fontFamily: '"All Round Gothic", AllRoundGothic, -apple-system, BlinkMacSystemFont, sans-serif', fontWeight: 700, fontStyle: 'normal', fontSize: 18, color: isDarkMode ? '#ffffff' : '#000000', letterSpacing: '-0.3px', lineHeight: 1.1 }}>t09n<span style={{ color: isDarkMode ? '#ffffff' : '#333333', fontWeight: 700 }}>.com</span></span>
                    <span style={{ fontFamily: '"All Round Gothic", AllRoundGothic, -apple-system, BlinkMacSystemFont, sans-serif', marginTop: 3, fontSize: 11, color: isDarkMode ? '#ffffff' : '#000000', fontWeight: 700, fontStyle: 'oblique', lineHeight: 1.2, letterSpacing: '0.1px' }}>The Operating System For Translators</span>
                  </div>
                </div>
              </div>
              <h2 style={{ textAlign: 'center', fontSize: 20, fontWeight: 700, color: isDarkMode ? '#f1f5f9' : '#111827', margin: '0 0 6px' }}>Proofreading</h2>
              <p style={{ textAlign: 'center', fontSize: 13, color: isDarkMode ? '#64748b' : '#6b7280', margin: '0 0 28px' }}>
                {['Uploading your document', 'Analysing content and style', 'Checking grammar and quality', 'Generating report'][proofStageIdx]}
              </p>
              <div style={{ position: 'relative', height: 6, background: isDarkMode ? '#1e293b' : '#f3f4f6', borderRadius: 6, marginBottom: 10, overflow: 'hidden' }}>
                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${proofQuality}%`, background: 'linear-gradient(90deg, #1d4ed8, #2563eb, #3b82f6)', borderRadius: 6, transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }} />
                <div style={{ position: 'absolute', top: 0, bottom: 0, width: '40%', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)', animation: 'review-shimmer 1.5s ease-in-out infinite' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 28, fontSize: 12 }}>
                <span style={{ color: '#2563eb', fontWeight: 600 }}>{proofQuality}%</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {proofStages.map(({ label, Icon }, idx) => {
                  const done = proofStageIdx > idx;
                  const active = proofStageIdx === idx;
                  return (
                    <div key={label} style={{ flex: 1, padding: '10px 6px', borderRadius: 12, textAlign: 'center', background: done ? (isDarkMode ? '#14532d' : '#f0fdf4') : active ? (isDarkMode ? '#1e293b' : '#f0f4ff') : (isDarkMode ? '#1e293b' : '#f9fafb'), border: `1px solid ${done ? (isDarkMode ? '#16a34a' : '#86efac') : active ? (isDarkMode ? '#60a5fa' : '#93c5fd') : (isDarkMode ? '#1e293b' : '#e5e7eb')}`, transition: 'all 0.4s ease' }}>
                      <Icon size={16} style={{ margin: '0 auto 6px', color: done ? (isDarkMode ? '#4ade80' : '#16a34a') : active ? (isDarkMode ? '#60a5fa' : '#2563eb') : (isDarkMode ? '#475569' : '#9ca3af'), display: 'block', transition: 'color 0.4s' }} />
                      <p style={{ fontSize: 11, fontWeight: 600, color: done ? (isDarkMode ? '#4ade80' : '#15803d') : active ? (isDarkMode ? '#60a5fa' : '#1d4ed8') : (isDarkMode ? '#475569' : '#9ca3af'), lineHeight: 1.3, margin: 0 }}>{label}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}
      {/* Translation QA overlay */}
      {(uploading || reviewingAll) && reviewMode === 'translation-qa' && (() => {
        const filledCount = Object.values(tqaScores).filter(v => v !== null).length;
        const tqaStageIdx = Math.min(filledCount, 3);
        const tqaProgress = (filledCount / 4) * 100;
        const tqaStages = [
          { label: 'Parsing Document', Icon: FileText },
          { label: 'Checking Terminology', Icon: Eye },
          { label: 'Scoring Segments', Icon: Calculator },
          { label: 'Report Ready', Icon: Sparkles },
        ];
        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', backgroundColor: isDarkMode ? 'rgba(10,15,26,0.92)' : 'rgba(255,255,255,0.95)' }}>
            <div style={{ background: isDarkMode ? '#111827' : '#ffffff', borderRadius: 16, padding: '40px 48px', width: '100%', maxWidth: 520, boxShadow: isDarkMode ? '0 20px 60px rgba(0,0,0,0.5)' : '0 20px 60px rgba(0,0,0,0.12)', border: isDarkMode ? '1px solid #1e293b' : '1px solid #e5e7eb', animation: 'review-overlay-in 0.2s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 44, height: 44, background: '#000', borderRadius: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: isDarkMode ? '0 0 0 2px #ffffff' : '0 0 0 1px rgba(0,86,255,.30), 0 4px 20px rgba(0,86,255,.22)' }}>
                    <img src="/logo-marketing.png" alt="t09n.com" style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 0, display: 'block' }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    <span style={{ fontFamily: '"All Round Gothic", AllRoundGothic, -apple-system, BlinkMacSystemFont, sans-serif', fontWeight: 700, fontStyle: 'normal', fontSize: 18, color: isDarkMode ? '#ffffff' : '#000000', letterSpacing: '-0.3px', lineHeight: 1.1 }}>t09n<span style={{ color: isDarkMode ? '#ffffff' : '#333333', fontWeight: 700 }}>.com</span></span>
                    <span style={{ fontFamily: '"All Round Gothic", AllRoundGothic, -apple-system, BlinkMacSystemFont, sans-serif', marginTop: 3, fontSize: 11, color: isDarkMode ? '#ffffff' : '#000000', fontWeight: 700, fontStyle: 'oblique', lineHeight: 1.2, letterSpacing: '0.1px' }}>The Operating System For Translators</span>
                  </div>
                </div>
              </div>
              <h2 style={{ textAlign: 'center', fontSize: 20, fontWeight: 700, color: isDarkMode ? '#f1f5f9' : '#111827', margin: '0 0 6px' }}>Translation Quality Check</h2>
              <p style={{ textAlign: 'center', fontSize: 13, color: isDarkMode ? '#64748b' : '#6b7280', margin: '0 0 28px' }}>
                {['Parsing source and target documents', 'Checking terminology consistency', 'Scoring translation segments', 'Generating QA report'][tqaStageIdx]}
              </p>
              <div style={{ position: 'relative', height: 6, background: isDarkMode ? '#1e293b' : '#f3f4f6', borderRadius: 6, marginBottom: 10, overflow: 'hidden' }}>
                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${tqaProgress}%`, background: 'linear-gradient(90deg, #1d4ed8, #2563eb, #3b82f6)', borderRadius: 6, transition: 'width 1.8s ease' }} />
                <div style={{ position: 'absolute', top: 0, bottom: 0, width: '40%', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)', animation: 'review-shimmer 1.5s ease-in-out infinite' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 28, fontSize: 12 }}>
                <span style={{ color: '#2563eb', fontWeight: 600 }}>{Math.round(tqaProgress)}%</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {tqaStages.map(({ label, Icon }, idx) => {
                  const done = tqaStageIdx > idx;
                  const active = tqaStageIdx === idx;
                  return (
                    <div key={label} style={{ flex: 1, padding: '10px 6px', borderRadius: 12, textAlign: 'center', background: done ? (isDarkMode ? '#14532d' : '#f0fdf4') : active ? (isDarkMode ? '#1e293b' : '#f0f4ff') : (isDarkMode ? '#1e293b' : '#f9fafb'), border: `1px solid ${done ? (isDarkMode ? '#16a34a' : '#86efac') : active ? (isDarkMode ? '#60a5fa' : '#93c5fd') : (isDarkMode ? '#1e293b' : '#e5e7eb')}`, transition: 'all 0.4s ease' }}>
                      <Icon size={16} style={{ margin: '0 auto 6px', color: done ? (isDarkMode ? '#4ade80' : '#16a34a') : active ? (isDarkMode ? '#60a5fa' : '#2563eb') : (isDarkMode ? '#475569' : '#9ca3af'), display: 'block', transition: 'color 0.4s' }} />
                      <p style={{ fontSize: 11, fontWeight: 600, color: done ? (isDarkMode ? '#4ade80' : '#15803d') : active ? (isDarkMode ? '#60a5fa' : '#1d4ed8') : (isDarkMode ? '#475569' : '#9ca3af'), lineHeight: 1.3, margin: 0 }}>{label}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}
      {/* Super Review overlay */}
      {srLoading && (() => {
        const srProgress = srCommentTicker.total > 0 ? (srCommentTicker.processed / srCommentTicker.total) * 100 : 5;
        const srStageIdx = srCommentTicker.total > 0 ? Math.min(Math.floor((srCommentTicker.processed / srCommentTicker.total) * 4), 3) : 0;
        const srStages = [
          { label: 'Reading Comments', Icon: MessageSquare },
          { label: 'Analysing Suggestions', Icon: Sparkles },
          { label: 'Accepting / Rejecting', Icon: Check },
          { label: 'Audit Report Ready', Icon: FileCheck },
        ];
        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', backgroundColor: isDarkMode ? 'rgba(10,15,26,0.92)' : 'rgba(255,255,255,0.95)' }}>
            <div style={{ background: isDarkMode ? '#111827' : '#ffffff', borderRadius: 16, padding: '40px 48px', width: '100%', maxWidth: 520, boxShadow: isDarkMode ? '0 20px 60px rgba(0,0,0,0.5)' : '0 20px 60px rgba(0,0,0,0.12)', border: isDarkMode ? '1px solid #1e293b' : '1px solid #e5e7eb', animation: 'review-overlay-in 0.2s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 44, height: 44, background: '#000', borderRadius: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: isDarkMode ? '0 0 0 2px #ffffff' : '0 0 0 1px rgba(0,86,255,.30), 0 4px 20px rgba(0,86,255,.22)' }}>
                    <img src="/logo-marketing.png" alt="t09n.com" style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 0, display: 'block' }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    <span style={{ fontFamily: '"All Round Gothic", AllRoundGothic, -apple-system, BlinkMacSystemFont, sans-serif', fontWeight: 700, fontStyle: 'normal', fontSize: 18, color: isDarkMode ? '#ffffff' : '#000000', letterSpacing: '-0.3px', lineHeight: 1.1 }}>t09n<span style={{ color: isDarkMode ? '#ffffff' : '#333333', fontWeight: 700 }}>.com</span></span>
                    <span style={{ fontFamily: '"All Round Gothic", AllRoundGothic, -apple-system, BlinkMacSystemFont, sans-serif', marginTop: 3, fontSize: 11, color: isDarkMode ? '#ffffff' : '#000000', fontWeight: 700, fontStyle: 'oblique', lineHeight: 1.2, letterSpacing: '0.1px' }}>The Operating System For Translators</span>
                  </div>
                </div>
              </div>
              <h2 style={{ textAlign: 'center', fontSize: 20, fontWeight: 700, color: isDarkMode ? '#f1f5f9' : '#111827', margin: '0 0 6px' }}>Super Review</h2>
              <p style={{ textAlign: 'center', fontSize: 13, color: isDarkMode ? '#64748b' : '#6b7280', margin: '0 0 28px' }}>
                {srCommentTicker.total > 0 ? `Processing ${srCommentTicker.processed} / ${srCommentTicker.total} comments` : 'Processing reviewer comments'}
              </p>
              <div style={{ position: 'relative', height: 6, background: isDarkMode ? '#1e293b' : '#f3f4f6', borderRadius: 6, marginBottom: 10, overflow: 'hidden' }}>
                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${srProgress}%`, background: 'linear-gradient(90deg, #1d4ed8, #2563eb, #3b82f6)', borderRadius: 6, transition: 'width 0.9s ease' }} />
                <div style={{ position: 'absolute', top: 0, bottom: 0, width: '40%', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)', animation: 'review-shimmer 1.5s ease-in-out infinite' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 28, fontSize: 12 }}>
                <span style={{ color: isDarkMode ? '#4ade80' : '#16a34a', fontWeight: 600 }}>✓ {srCommentTicker.accepted} accepted</span>
                <span style={{ color: '#2563eb', fontWeight: 600 }}>{Math.round(srProgress)}%</span>
                <span style={{ color: isDarkMode ? '#f87171' : '#dc2626', fontWeight: 600 }}>✗ {srCommentTicker.rejected} rejected</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {srStages.map(({ label, Icon }, idx) => {
                  const done = srStageIdx > idx;
                  const active = srStageIdx === idx;
                  return (
                    <div key={label} style={{ flex: 1, padding: '10px 6px', borderRadius: 12, textAlign: 'center', background: done ? (isDarkMode ? '#14532d' : '#f0fdf4') : active ? (isDarkMode ? '#1e293b' : '#f0f4ff') : (isDarkMode ? '#1e293b' : '#f9fafb'), border: `1px solid ${done ? (isDarkMode ? '#16a34a' : '#86efac') : active ? (isDarkMode ? '#60a5fa' : '#93c5fd') : (isDarkMode ? '#1e293b' : '#e5e7eb')}`, transition: 'all 0.4s ease' }}>
                      <Icon size={16} style={{ margin: '0 auto 6px', color: done ? (isDarkMode ? '#4ade80' : '#16a34a') : active ? (isDarkMode ? '#60a5fa' : '#2563eb') : (isDarkMode ? '#475569' : '#9ca3af'), display: 'block', transition: 'color 0.4s' }} />
                      <p style={{ fontSize: 11, fontWeight: 600, color: done ? (isDarkMode ? '#4ade80' : '#15803d') : active ? (isDarkMode ? '#60a5fa' : '#1d4ed8') : (isDarkMode ? '#475569' : '#9ca3af'), lineHeight: 1.3, margin: 0 }}>{label}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}
      {/* Context Selector Modal */}
      <ContextSelectorModal
        isOpen={showContextModal} 
        onClose={() => setShowContextModal(false)} 
        variant="apple"
      />
      
      <div className={`p-6 min-h-screen ${isDarkMode ? 'bg-gray-950' : 'bg-gray-50'}`} data-testid="review-page">
        {/* Global Context Bar */}
        <div className="mb-4">
          <GlobalContextBarApple 
            onOpenModal={() => setShowContextModal(true)} 
            isDarkMode={isDarkMode}
          />
        </div>
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            {view === 'review' && (
              <button 
                onClick={() => setView('upload')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft size={18} />
                Back
              </button>
            )}
            <h1 className="text-2xl font-semibold text-gray-900">Review & Proofreading</h1>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => { setView('history'); loadHistory(); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${view === 'history' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <History size={18} />
              History
            </button>
            {view === 'review' && (
              <button 
                onClick={() => setShowExportModal(true)}
                className="flex items-center gap-2 px-5 py-2.5 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 transition-all"
              >
                <Download size={18} />
                Export
              </button>
            )}
          </div>
        </div>

        {/* Mode Selection View */}
        {reviewMode === 'select' && view === 'upload' && (
          <div className="max-w-2xl mx-auto">
            <div className={`rounded-3xl p-8 shadow-sm ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
              <h2 className="text-xl font-semibold text-gray-900 text-center mb-2">
                What type of review do you need?
              </h2>
              <p className="text-gray-500 text-center mb-8">
                Choose the service that best fits your needs
              </p>
              
              <div className="space-y-4">
                {/* Proofreading Option */}
                <button
                  onClick={() => setReviewMode('proofread')}
                  className="w-full p-6 rounded-2xl border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all text-left group"
                  data-testid="mode-proofreading"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                      <Edit3 size={24} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                        Proofreading
                      </h3>
                      <p className="text-gray-500 mt-1">
                        Fix grammar, spelling, punctuation, and style issues
                      </p>
                      <div className="flex items-center gap-4 mt-3 text-sm">
                        <span className="flex items-center gap-1.5 text-gray-600">
                          <FileText size={14} />
                          Upload: 1 document
                        </span>
                        <span className="flex items-center gap-1.5 text-green-600 font-medium">
                          Cost: 30% of word count
                        </span>
                      </div>
                    </div>
                    <ArrowRight size={20} className="text-gray-400 group-hover:text-blue-500 transition-colors mt-1" />
                  </div>
                </button>
                
                {/* Translation QA Option */}
                <button
                  onClick={() => setReviewMode('translation-qa')}
                  className="w-full p-6 rounded-2xl border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all text-left group"
                  data-testid="mode-translation-qa"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                      <Eye size={24} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                        Translation Quality Check
                      </h3>
                      <p className="text-gray-500 mt-1">
                        Verify translation accuracy, terminology, and instruction compliance
                      </p>
                      <div className="flex items-center gap-4 mt-3 text-sm">
                        <span className="flex items-center gap-1.5 text-gray-600">
                          <FileText size={14} />
                          Upload: Source + Translation
                        </span>
                        <span className="flex items-center gap-1.5 text-amber-600 font-medium">
                          Cost: 50% of word count
                        </span>
                      </div>
                    </div>
                    <ArrowRight size={20} className="text-gray-400 group-hover:text-blue-500 transition-colors mt-1" />
                  </div>
                </button>

                {/* Super Review */}
                <button
                  onClick={() => setReviewMode('super-review')}
                  className="w-full p-6 rounded-xl border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all text-left group"
                  data-testid="mode-super-review"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                      <Sparkles size={24} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">Super Review</h3>
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">NEW</span>
                      </div>
                      <p className="text-gray-500 mt-1">Apply reviewer comments intelligently — get a clean final version, highlighted changes, and full resolution report</p>
                      <div className="flex items-center gap-4 mt-3 text-sm">
                        <span className="flex items-center gap-1.5 text-gray-600"><FileText size={14} />Source + Translation + Comments</span>
                        <span className="flex items-center gap-1.5 text-emerald-600 font-medium">Cost: 50% of word count</span>
                      </div>
                    </div>
                    <ArrowRight size={20} className="text-gray-400 group-hover:text-blue-500 transition-colors mt-1" />
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Proofreading Upload View */}
        {reviewMode === 'proofread' && view === 'upload' && (
          <div className="max-w-3xl mx-auto space-y-5">
            {/* Back */}
            <button onClick={() => setReviewMode('select')} className="flex items-center gap-2 text-gray-500 hover:text-gray-800 text-sm transition-colors">
              <ArrowLeft size={16} /> Choose Different Mode
            </button>

            {/* Header */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                  <PenTool size={20} className="text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Proofreading</h2>
                  <p className="text-sm text-gray-500">Professional grammar, style, and compliance check</p>
                </div>
              </div>
            </div>

            {/* Proofreading Mode Selector */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wider">Proofreading Depth</h3>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { id: 'light', label: 'Light', desc: 'Grammar & spelling only', icon: '⚡', color: 'blue',
                    checks: {grammar: true, spelling: true, punctuation: false, style: false, consistency: false, tone: false} },
                  { id: 'standard', label: 'Standard', desc: 'Grammar + style + tone', icon: '✦', color: 'purple',
                    checks: {grammar: true, spelling: true, punctuation: true, style: true, consistency: true, tone: false} },
                  { id: 'deep', label: 'Deep', desc: 'All checks + Style Notes', icon: '🎯', color: 'green',
                    checks: {grammar: true, spelling: true, punctuation: true, style: true, consistency: true, tone: true} },
                ].map(mode => (
                  <button
                    key={mode.id}
                    onClick={() => { setChecks(mode.checks); }}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      JSON.stringify(checks) === JSON.stringify(mode.checks)
                        ? mode.color === 'blue' ? 'border-blue-500 bg-blue-50' : mode.color === 'purple' ? 'border-purple-500 bg-purple-50' : 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-xl mb-2">{mode.icon}</div>
                    <div className={`text-sm font-bold mb-1 ${
                      JSON.stringify(checks) === JSON.stringify(mode.checks)
                        ? mode.color === 'blue' ? 'text-blue-700' : mode.color === 'purple' ? 'text-purple-700' : 'text-green-700'
                        : 'text-gray-800'
                    }`}>{mode.label}</div>
                    <div className="text-xs text-gray-500">{mode.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* File Upload */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wider">Document</h3>
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${file ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-purple-400 hover:bg-purple-50'}`}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => fileInputRef.current?.click()}
              >
                <input ref={fileInputRef} type="file" accept={FILE_ACCEPT_STRING} onChange={handleFileChange} hidden />
                {file ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileCheck size={32} className="text-green-500 flex-shrink-0" />
                      <div className="text-left">
                        <p className="font-semibold text-gray-900">{file.name}</p>
                        <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                      </div>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); setFile(null); }} className="text-sm text-gray-400 hover:text-gray-600 px-3 py-1 rounded-lg hover:bg-gray-100">Change</button>
                  </div>
                ) : (
                  <>
                    <FileUp size={40} className="mx-auto mb-3 text-gray-300" />
                    <p className="font-semibold text-gray-700 mb-1">Drop file here or click to upload</p>
                    <p className="text-xs text-gray-400">DOCX · XLSX · PDF · TXT</p>
                  </>
                )}
              </div>
            </div>

            {/* Language + Style Notes */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Document Language</h3>
                <select value={targetLang} onChange={(e) => setTargetLang(e.target.value)} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-400">
                  {LANGUAGES.map(lang => <option key={lang} value={lang}>{lang}</option>)}
                </select>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Style Notes <span className="text-gray-400 font-normal normal-case">(optional)</span></h3>
                <textarea
                  value={proofreadStyleNotes}
                  onChange={(e) => setProofreadStyleNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
                  rows={3}
                  placeholder="E.g. Use formal tone, avoid passive voice, this is a legal document..."
                />
              </div>
            </div>

            {/* Cost Estimate */}
            {file && sourceWordCount !== 0 && (
              <CostEstimateApple feature="proofread" sourceWords={sourceWordCount} />
            )}

            {/* Start Button */}
            <div style={{ display: uploading ? 'flex' : 'block', gap: 16, alignItems: 'stretch' }}>
              <button
                onClick={handleStartReview}
                disabled={!file || uploading}
                className="w-full py-4 bg-purple-600 text-white rounded-2xl font-bold text-base hover:bg-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm"
                style={{ flex: uploading ? 1 : undefined }}
              >
                {uploading ? (
                  <><Loader2 size={20} className="animate-spin" /> Uploading & Analyzing...</>
                ) : (
                  <><Sparkles size={20} /> Start Proofreading</>
                )}
              </button>
              {uploading && (
                <div style={{ width: 40, flexShrink: 0, background: isDarkMode ? '#1e293b' : '#f3f4f6', borderRadius: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', padding: '6px 0', overflow: 'hidden', position: 'relative' }}>
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: `${proofQuality}%`, background: proofQuality < 40 ? '#9ca3af' : proofQuality < 70 ? '#2563eb' : '#16a34a', transition: 'height 0.4s cubic-bezier(0.4, 0, 0.2, 1), background 0.6s ease', borderRadius: '12px 12px 0 0' }} />
                  <span style={{ position: 'relative', fontSize: 11, color: '#374151', fontWeight: 700, zIndex: 1 }}>{proofQuality}%</span>
                </div>
              )}
            </div>
            {uploading && (
              <p style={{ textAlign: 'center', fontSize: 12, color: '#7c3aed', marginTop: 8 }}>
                {sourceWordCount > 0 ? `Checking ${Math.round(proofQuality / 100 * sourceWordCount)} / ${sourceWordCount} words` : 'Analysing...'} · {proofQuality < 50 ? '0' : Math.floor((proofQuality - 50) / 10)} issues flagged
              </p>
            )}
          </div>
        )}

        {/* Translation QA Upload View */}
        {reviewMode === 'translation-qa' && view === 'upload' && (
          <div className="max-w-3xl mx-auto space-y-6">
            {/* Back to mode selection */}
            <button 
              onClick={() => setReviewMode('select')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-4"
            >
              <ArrowLeft size={18} />
              Choose Different Mode
            </button>

            {/* Source File Upload */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">1</div>
                Source Document
              </h3>
              <div 
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${sourceFile ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50'}`}
                onClick={() => sourceFileInputRef.current?.click()}
              >
                <input 
                  ref={sourceFileInputRef}
                  type="file" 
                  accept={FILE_ACCEPT_STRING}
                  onChange={(e) => setSourceFile(e.target.files?.[0] || null)}
                  hidden
                />
                {sourceFile ? (
                  <>
                    <FileCheck size={36} className="mx-auto mb-3 text-green-500" />
                    <h4 className="font-semibold text-gray-900">{sourceFile.name}</h4>
                    <p className="text-sm text-gray-500 mt-1">{(sourceFile.size / 1024).toFixed(1)} KB</p>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setSourceFile(null); }}
                      className="text-sm text-gray-500 hover:text-gray-700 mt-2"
                    >
                      Change File
                    </button>
                  </>
                ) : (
                  <>
                    <FileUp size={36} className="mx-auto mb-3 text-gray-400" />
                    <p className="text-gray-600 font-medium">Upload source document</p>
                    <p className="text-sm text-gray-400 mt-1">Original text before translation</p>
                  </>
                )}
              </div>
              <div className="mt-4">
                <label className="text-sm font-medium text-gray-700 block mb-2">Source Language</label>
                <select 
                  value={sourceLang}
                  onChange={(e) => setSourceLang(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                  {LANGUAGES.map(lang => (
                    <option key={lang} value={lang}>{lang}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Target File Upload */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">2</div>
                Translation Document
              </h3>
              <div 
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${targetFile ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50'}`}
                onClick={() => targetFileInputRef.current?.click()}
              >
                <input 
                  ref={targetFileInputRef}
                  type="file" 
                  accept={FILE_ACCEPT_STRING}
                  onChange={(e) => setTargetFile(e.target.files?.[0] || null)}
                  hidden
                />
                {targetFile ? (
                  <>
                    <FileCheck size={36} className="mx-auto mb-3 text-green-500" />
                    <h4 className="font-semibold text-gray-900">{targetFile.name}</h4>
                    <p className="text-sm text-gray-500 mt-1">{(targetFile.size / 1024).toFixed(1)} KB</p>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setTargetFile(null); }}
                      className="text-sm text-gray-500 hover:text-gray-700 mt-2"
                    >
                      Change File
                    </button>
                  </>
                ) : (
                  <>
                    <FileUp size={36} className="mx-auto mb-3 text-gray-400" />
                    <p className="text-gray-600 font-medium">Upload translation document</p>
                    <p className="text-sm text-gray-400 mt-1">Translated text to be reviewed</p>
                  </>
                )}
              </div>
              <div className="mt-4">
                <label className="text-sm font-medium text-gray-700 block mb-2">Target Language</label>
                <select 
                  value={targetLang}
                  onChange={(e) => setTargetLang(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                  {LANGUAGES.map(lang => (
                    <option key={lang} value={lang}>{lang}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* QA Checks Selection */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Quality Checks</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'accuracy', label: 'Translation Accuracy', desc: 'Verify meaning is preserved' },
                  { key: 'completeness', label: 'Completeness', desc: 'Check for omissions/additions' },
                  { key: 'terminology', label: 'Terminology', desc: 'Consistent technical terms' },
                  { key: 'instructionCompliance', label: 'Instruction Compliance', desc: 'Follow project rules' },
                  { key: 'grammar', label: 'Target Grammar', desc: 'Target language quality' },
                  { key: 'style', label: 'Style & Tone', desc: 'Match source style' },
                  { key: 'cultural', label: 'Cultural Fit', desc: 'Cultural appropriateness' }
                ].map(check => (
                  <label 
                    key={check.key}
                    className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-all border ${tqaChecks[check.key] ? 'border-blue-200 bg-blue-50' : 'border-gray-100 hover:border-gray-200'}`}
                  >
                    <input 
                      type="checkbox"
                      checked={tqaChecks[check.key]}
                      onChange={(e) => setTqaChecks(prev => ({ ...prev, [check.key]: e.target.checked }))}
                      className="mt-1 accent-blue-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900 block">{check.label}</span>
                      <span className="text-xs text-gray-500">{check.desc}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Cost Estimate */}
            {(sourceFile || targetFile) && (sourceWordCount !== 0 || targetWordCount !== 0) && (
              <CostEstimateApple 
                feature="translation_qa"
                sourceWords={sourceWordCount}
                targetWords={targetWordCount}
              />
            )}

            {/* Active Context Indicator */}
            <ActiveContextBadgesApple variant="full" showEmpty={true} />

            {/* Start Button */}
            <button 
              onClick={handleStartTranslationQA}
              disabled={!sourceFile || !targetFile || uploading}
              className="w-full py-4 bg-blue-500 text-white rounded-xl font-semibold text-lg hover:bg-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {uploading ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Eye size={20} />
                  Start Translation QA
                </>
              )}
            </button>
          </div>
        )}

        {/* Review View */}
        {view === 'review' && review && (
          <div className="flex flex-col h-[calc(100vh-200px)]">
            {/* 3-Panel Layout */}
            <div className="grid grid-cols-[220px_1fr_300px] gap-4 flex-1 overflow-hidden">
              {/* Left Panel */}
              <div className="bg-white rounded-2xl shadow-sm flex flex-col overflow-hidden">
                <div className="p-4 border-b border-gray-100 bg-gray-50">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                    <LayoutList size={16} />
                    {review?.mode === 'translation-qa' ? 'Document Structure' : 'Paragraphs'}
                  </div>
                  {/* Smart Review All Button */}
                  <button
                    onClick={handleAutoReviewAll}
                    disabled={reviewingAll}
                    className="w-full py-2 text-sm bg-[#0071e3] text-white rounded-xl hover:bg-[#0077ed] disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="smart-review-all-btn"
                  >
                    {reviewingAll ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        {review?.mode === 'translation-qa' ? 'Reviewing...' : 'Proofreading...'}
                      </>
                    ) : (
                      <>
                        <Sparkles size={14} />
                        {review?.mode === 'translation-qa' ? 'Smart Review All' : 'Smart Proofread All'}
                      </>
                    )}
                  </button>
                  
                  {/* Cost Estimate */}
                  {review && (
                    <div className="mt-3 p-2 bg-[#0071e3]/5 rounded-xl text-xs">
                      <div className="font-medium text-[#0071e3]">Cost Estimate</div>
                      <div className="text-gray-600">
                        {review.mode === 'translation-qa' 
                          ? `Target: ${(review.total_words || 0).toLocaleString()} words`
                          : `Words: ${(review.total_words || 0).toLocaleString()}`
                        }
                      </div>
                      <div className="text-[#0071e3] font-medium">
                        Cost: {review.mode === 'translation-qa' 
                          ? Math.ceil((review.total_words || 0) * 0.5).toLocaleString()
                          : Math.ceil((review.total_words || 0) * 0.3).toLocaleString()
                        } words
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto p-2">
                  {segments.map((seg, idx) => (
                    <div 
                      key={seg.segment_id}
                      onClick={() => setActiveSegmentId(seg.segment_id)}
                      className={`flex items-center gap-2 p-3 rounded-xl cursor-pointer transition-all mb-1 ${activeSegmentId === seg.segment_id ? 'bg-blue-100' : 'hover:bg-gray-50'} ${seg.status === 'reviewed' ? 'border-l-4 border-green-500' : seg.status === 'accepted' ? 'border-l-4 border-blue-500' : ''}`}
                    >
                      <span className="text-xs font-semibold text-gray-400 w-6">#{idx + 1}</span>
                      <span className="text-xs text-gray-700 flex-1 truncate">{seg.original_text.substring(0, 25)}...</span>
                      {seg.status === 'reviewed' && <CheckCircle size={14} className="text-green-500" />}
                      {seg.status === 'accepted' && <Check size={14} className="text-blue-500" />}
                      {seg.issues?.length > 0 && seg.status === 'pending' && (
                        <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs font-semibold rounded-full">{seg.issues.length}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Center Panel */}
              <div className="bg-white rounded-2xl shadow-sm flex flex-col overflow-hidden">
                <div className="p-4 border-b border-gray-100 bg-gray-50 flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <Edit3 size={16} />
                  Review Editor
                  {activeSegment && (
                    <span className="ml-auto text-xs text-gray-400 font-normal">
                      Segment {segments.findIndex(s => s.segment_id === activeSegmentId) + 1} of {segments.length}
                    </span>
                  )}
                </div>
                
                {activeSegment ? (
                  <div className="flex-1 overflow-y-auto p-5 space-y-5">
                    {/* Original */}
                    <div>
                      <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 block">Original</label>
                      <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 text-base leading-relaxed">
                        {renderTextWithHighlights(activeSegment.original_text, activeSegment.issues)}
                      </div>
                    </div>

                    {/* Manual Edit Mode */}
                    {manualEditMode ? (
                      <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-200">
                        <label className="text-xs font-semibold text-yellow-700 uppercase tracking-wider mb-2 block flex items-center gap-2">
                          <PenTool size={14} />
                          Manual Review
                        </label>
                        <textarea
                          value={manualEditText}
                          onChange={(e) => setManualEditText(e.target.value)}
                          className="w-full p-3 border border-yellow-200 rounded-lg text-base leading-relaxed min-h-32 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                          placeholder="Enter your reviewed text..."
                        />
                        <div className="mt-3">
                          <label className="text-xs font-semibold text-yellow-700 mb-2 block">Add Comment (Optional)</label>
                          <textarea
                            value={manualComment}
                            onChange={(e) => setManualComment(e.target.value)}
                            className="w-full p-3 border border-yellow-200 rounded-lg text-sm min-h-20 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                            placeholder="Explain your changes..."
                          />
                        </div>
                        <div className="flex gap-2 mt-3">
                          <button 
                            onClick={handleSaveManualReview}
                            disabled={savingManualReview}
                            className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg font-semibold hover:bg-yellow-600 disabled:opacity-50"
                          >
                            {savingManualReview ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                            Save Manual Review
                          </button>
                          <button 
                            onClick={() => { setManualEditMode(false); setManualEditText(''); setManualComment(''); }}
                            className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg font-semibold hover:bg-gray-200"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Reviewed */}
                        {activeSegment.reviewed_text && (
                          <div>
                            <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 block">Reviewed</label>
                            <div className="p-4 bg-green-50 rounded-xl border border-green-100 text-base leading-relaxed">
                              {activeSegment.reviewed_text}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* Changes */}
                    {!manualEditMode && activeSegment.changes && activeSegment.changes.length > 0 && (
                      <div>
                        <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 block">Changes & Comments</label>
                        {activeSegment.changes.map((change, idx) => (
                          <div key={idx} className={`p-4 bg-white border rounded-xl mb-3 border-l-4 ${change.status === 'accepted' ? 'border-l-green-500 bg-green-50' : change.status === 'rejected' ? 'border-l-red-500 opacity-60' : change.comment_type === 'manual' ? 'border-l-yellow-500 bg-yellow-50' : 'border-l-blue-500'}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <MessageSquare size={14} className={change.comment_type === 'manual' ? 'text-yellow-600' : 'text-blue-500'} />
                              {change.original && change.corrected ? (
                                <span className="text-sm font-semibold text-gray-900 flex-1">"{change.original}" → "{change.corrected}"</span>
                              ) : (
                                <span className="text-sm font-semibold text-gray-900 flex-1">Manual Comment</span>
                              )}
                              <span className={`text-xs px-2 py-1 rounded-lg font-semibold capitalize ${change.comment_type === 'manual' ? 'bg-yellow-100 text-yellow-700' : change.type === 'grammar' || change.type === 'spelling' ? 'bg-red-100 text-red-600' : change.type === 'style' || change.type === 'punctuation' ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'}`}>
                                {change.comment_type === 'manual' ? 'manual' : change.type}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mb-3">{change.comment}</p>
                            {change.status === 'proposed' && (
                              <div className="flex gap-2">
                                <button 
                                  onClick={() => handleAcceptChanges([change.change_id])}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-sm font-semibold hover:bg-green-200 transition-all"
                                >
                                  <Check size={14} /> Accept
                                </button>
                                <button 
                                  onClick={() => handleRejectChanges([change.change_id])}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm font-semibold hover:bg-red-200 transition-all"
                                >
                                  <X size={14} /> Reject
                                </button>
                              </div>
                            )}
                            {change.status !== 'proposed' && (
                              <span className={`text-xs font-semibold flex items-center gap-1 ${change.status === 'accepted' ? 'text-green-600' : 'text-red-600'}`}>
                                {change.status === 'accepted' ? <Check size={12} /> : <X size={12} />}
                                {change.status === 'accepted' ? 'Accepted' : 'Rejected'}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-100">
                      {!manualEditMode && (
                        <>
                          {activeSegment.status === 'pending' && (
                            <button 
                              onClick={() => handleAutoReviewSegment(activeSegment.segment_id)}
                              disabled={reviewingSegment === activeSegment.segment_id}
                              className="flex items-center gap-2 px-5 py-2.5 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 transition-all disabled:opacity-50"
                            >
                              {reviewingSegment === activeSegment.segment_id ? (
                                <>
                                  <Loader2 size={18} className="animate-spin" />
                                  Reviewing...
                                </>
                              ) : (
                                <>
                                  <Sparkles size={18} />
                                  {review?.mode === 'translation-qa' ? 'Smart Review' : 'Smart Proofread'}
                                </>
                              )}
                            </button>
                          )}
                          
                          {/* Manual Review Button */}
                          <button 
                            onClick={startManualEdit}
                            className="flex items-center gap-2 px-4 py-2.5 bg-yellow-500 text-white rounded-xl font-semibold hover:bg-yellow-600 transition-all"
                          >
                            <PenTool size={18} />
                            Manual Review
                          </button>
                          
                          {activeSegment.changes?.some(c => c.status === 'proposed') && (
                            <>
                              <button 
                                onClick={() => handleAcceptChanges(activeSegment.changes.filter(c => c.status === 'proposed').map(c => c.change_id))}
                                className="flex items-center gap-2 px-4 py-2.5 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-all"
                              >
                                <Check size={18} /> Accept All
                              </button>
                              <button 
                                onClick={() => handleRejectChanges(activeSegment.changes.filter(c => c.status === 'proposed').map(c => c.change_id))}
                                className="flex items-center gap-2 px-4 py-2.5 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 transition-all"
                              >
                                <X size={18} /> Reject All
                              </button>
                            </>
                          )}
                        </>
                      )}

                      <div className="flex gap-2 ml-auto">
                        <button 
                          onClick={() => {
                            const idx = segments.findIndex(s => s.segment_id === activeSegmentId);
                            if (idx > 0) setActiveSegmentId(segments[idx - 1].segment_id);
                          }}
                          disabled={segments.findIndex(s => s.segment_id === activeSegmentId) === 0}
                          className="flex items-center gap-1 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-30"
                        >
                          <ArrowLeft size={16} /> Prev
                        </button>
                        <button 
                          onClick={() => {
                            const idx = segments.findIndex(s => s.segment_id === activeSegmentId);
                            if (idx < segments.length - 1) setActiveSegmentId(segments[idx + 1].segment_id);
                          }}
                          disabled={segments.findIndex(s => s.segment_id === activeSegmentId) === segments.length - 1}
                          className="flex items-center gap-1 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-30"
                        >
                          Next <ArrowRight size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-gray-400">
                    <div className="text-center">
                      <FileText size={48} className="mx-auto mb-2 opacity-50" />
                      <p>Select a segment from the left panel</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Panel */}
              <div className="bg-white rounded-2xl shadow-sm flex flex-col overflow-hidden">
                <div className={`p-4 border-b border-gray-100 flex items-center gap-2 text-sm font-semibold ${review?.mode === 'translation-qa' ? 'bg-gray-50 text-gray-700' : 'bg-purple-50 text-purple-700'}`}>
                  {review?.mode === 'translation-qa' ? <AlertTriangle size={16} /> : <PenTool size={16} />}
                  {review?.mode === 'translation-qa' ? 'Issues & Actions' : 'Proofread Report'}
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: 'Issues', value: totalIssues, color: totalIssues > 0 ? 'text-red-500' : 'text-gray-900' },
                      { label: 'Reviewed', value: reviewedCount, color: 'text-blue-500' },
                      { label: 'Pending', value: pendingCount, color: 'text-orange-500' },
                      { label: 'Accepted', value: acceptedChanges, color: 'text-green-500' }
                    ].map((stat, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-xl p-3 text-center">
                        <span className="text-xs text-gray-400 block mb-1">{stat.label}</span>
                        <span className={`text-xl font-bold ${stat.color}`}>{stat.value}</span>
                      </div>
                    ))}
                  </div>

                  {/* Issue Breakdown - Proofread mode */}
                  {review?.mode !== 'translation-qa' && totalIssues > 0 && (
                    <div className="bg-gray-50 rounded-xl p-3">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">By Type</h4>
                      {['grammar', 'spelling', 'punctuation', 'style', 'consistency', 'instruction_violation', 'glossary_violation'].map(type => {
                        const count = segments.flatMap(s => s.changes || []).filter(c => c.type === type).length;
                        if (count === 0) return null;
                        const colors = {
                          grammar: 'bg-red-100 text-red-600',
                          spelling: 'bg-red-100 text-red-600',
                          punctuation: 'bg-orange-100 text-orange-600',
                          style: 'bg-yellow-100 text-yellow-700',
                          consistency: 'bg-blue-100 text-blue-600',
                          instruction_violation: 'bg-purple-100 text-purple-600',
                          glossary_violation: 'bg-pink-100 text-pink-600'
                        };
                        return (
                          <div key={type} className="flex items-center justify-between py-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${colors[type] || 'bg-gray-100 text-gray-600'}`}>
                              {type.replace('_', ' ')}
                            </span>
                            <span className="text-sm font-bold text-gray-700">{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Bulk Actions */}
                  <div className="space-y-2">
                    <button
                      onClick={handleAutoReviewAll}
                      disabled={reviewingAll || pendingCount === 0}
                      className={`w-full flex items-center justify-center gap-2 py-2.5 text-white rounded-xl font-semibold transition-all disabled:opacity-50 text-sm ${review?.mode === 'translation-qa' ? 'bg-blue-500 hover:bg-blue-600' : 'bg-purple-600 hover:bg-purple-700'}`}
                    >
                      {reviewingAll ? <><Loader2 size={16} className="animate-spin" />{review?.mode === 'translation-qa' ? 'Reviewing...' : 'Proofreading...'}</> 
                        : <><Sparkles size={16} />{review?.mode === 'translation-qa' ? `Smart Review All (${pendingCount})` : `Smart Proofread All (${pendingCount})`}</>}
                    </button>
                    <button
                      onClick={handleAcceptAll}
                      disabled={segments.every(s => !s.changes?.some(c => c.status === 'proposed'))}
                      className="w-full flex items-center justify-center gap-2 py-2.5 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-all disabled:opacity-50 text-sm"
                    >
                      <Check size={16} /> Accept All Changes
                    </button>
                    <button
                      onClick={handleRejectAll}
                      disabled={segments.every(s => !s.changes?.some(c => c.status === 'proposed'))}
                      className="w-full flex items-center justify-center gap-2 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all disabled:opacity-50 text-sm border border-gray-200"
                    >
                      <X size={16} /> Reject All Changes
                    </button>
                  </div>

                  {/* All Issues list */}
                  <div>
                    <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">All Issues</h4>
                    <div className="space-y-1.5 max-h-56 overflow-y-auto">
                      {segments.flatMap(seg =>
                        (seg.changes || seg.issues || []).filter(c => c.status !== 'rejected').map((issue, idx) => (
                          <div
                            key={`${seg.segment_id}-${idx}`}
                            onClick={() => setActiveSegmentId(seg.segment_id)}
                            className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-all"
                          >
                            <span className={`text-xs px-1.5 py-0.5 rounded font-semibold capitalize flex-shrink-0 ${
                              issue.type === 'grammar' || issue.type === 'spelling' ? 'bg-red-100 text-red-600' :
                              issue.type === 'style' || issue.type === 'punctuation' ? 'bg-orange-100 text-orange-600' :
                              issue.type === 'instruction_violation' ? 'bg-purple-100 text-purple-600' :
                              issue.type === 'glossary_violation' ? 'bg-pink-100 text-pink-600' :
                              'bg-blue-100 text-blue-600'
                            }`}>{issue.type?.replace('_violation','')?.substring(0, 5)}</span>
                            <span className="text-xs text-gray-600 flex-1 truncate">{(issue.original || issue.original_text || '—').substring(0, 20)}</span>
                          </div>
                        ))
                      ).slice(0, 20)}
                      {totalIssues > 20 && <p className="text-xs text-gray-400 text-center pt-1">+{totalIssues - 20} more</p>}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Bar */}
            <div className="mt-4 p-4 bg-white/80 backdrop-blur-xl rounded-2xl flex items-center justify-center gap-10 shadow-sm">
              {[
                { label: 'Document', value: review.file_name },
                { label: 'Segments', value: segments.length },
                { label: 'Issues', value: totalIssues },
                { label: 'Reviewed', value: reviewedCount },
                { label: 'Accepted', value: acceptedChanges, success: true }
              ].map((item, idx) => (
                <div key={idx} className="flex items-baseline gap-2">
                  <span className="text-sm text-gray-500">{item.label}:</span>
                  <span className={`text-base font-semibold ${item.success ? 'text-green-500' : 'text-gray-900'}`}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* History View */}
        {view === 'history' && (
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Review History</h2>
              <button 
                onClick={() => setView('upload')}
                className="flex items-center gap-2 px-5 py-2.5 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 transition-all"
              >
                <FileUp size={18} /> New Review
              </button>
            </div>
            
            {loadingHistory ? (
              <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                <Loader2 size={32} className="animate-spin mb-4" />
                <p>Loading history...</p>
              </div>
            ) : history.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                <History size={48} className="mb-4 opacity-50" />
                <p>No review history yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {history.map(rev => (
                  <div 
                    key={rev.review_id}
                    onClick={() => loadReview(rev.review_id)}
                    className="bg-white rounded-2xl p-5 flex items-center gap-4 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all"
                  >
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <FileText size={24} className="text-blue-500" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{rev.file_name}</h4>
                      <p className="text-sm text-gray-500">{rev.total_segments} segments • {rev.issues_detected} issues • {rev.changes_accepted} accepted</p>
                      <span className="text-xs text-gray-400">{new Date(rev.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</span>
                    </div>
                    <div className={`${rev.status === 'completed' ? 'text-green-500' : 'text-gray-400'}`}>
                      {rev.status === 'completed' ? <CheckCircle size={24} /> : <Loader2 size={24} />}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Export Modal */}
        {showExportModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm" onClick={() => setShowExportModal(false)}>
            <div className={`rounded-3xl p-7 max-w-md w-full mx-4 shadow-2xl ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`} onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Export Reviewed Document</h3>
                <button onClick={() => setShowExportModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={20} />
                </button>
              </div>
              
              {/* Show file format info */}
              {review && (
                <div className="mb-4 p-3 bg-gray-50 rounded-xl">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Export format:</span>{' '}
                    <span className="text-blue-600 font-semibold uppercase">
                      {review.file_name?.split('.').pop() || 'TXT'}
                    </span>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {review.file_name?.toLowerCase().endsWith('.docx') && 'Exports with real Word comments in margin'}
                    {review.file_name?.toLowerCase().endsWith('.xlsx') && 'Exports with real Excel cell comments (hover to view)'}
                    {review.file_name?.toLowerCase().endsWith('.pdf') && 'Exports with PDF annotations (sticky notes)'}
                    {review.file_name?.toLowerCase().endsWith('.txt') && 'Exports with text comment markers'}
                  </p>
                </div>
              )}
              
              <div className="space-y-3 mb-6">
                {/* Clean Version - Available for all */}
                <label 
                  className={`flex items-start gap-3 p-4 rounded-xl cursor-pointer border-2 transition-all ${exportType === 'clean' ? 'border-blue-500 bg-blue-50' : 'border-transparent bg-gray-50 hover:bg-gray-100'}`}
                >
                  <input 
                    type="radio" 
                    name="exportType" 
                    value="clean"
                    checked={exportType === 'clean'}
                    onChange={(e) => setExportType(e.target.value)}
                    className="mt-1 accent-blue-500"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">✨ Clean Version</h4>
                    <p className="text-sm text-gray-500">All corrections applied, no comments or markings</p>
                  </div>
                </label>
                
                {/* Comments - Format-specific description */}
                <label 
                  className={`flex items-start gap-3 p-4 rounded-xl cursor-pointer border-2 transition-all ${exportType === 'comments' ? 'border-blue-500 bg-blue-50' : 'border-transparent bg-gray-50 hover:bg-gray-100'}`}
                >
                  <input 
                    type="radio" 
                    name="exportType" 
                    value="comments"
                    checked={exportType === 'comments'}
                    onChange={(e) => setExportType(e.target.value)}
                    className="mt-1 accent-blue-500"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">💬 With Real Comments</h4>
                    <p className="text-sm text-gray-500">
                      {review?.file_name?.toLowerCase().endsWith('.docx') && 'Word comments in right margin'}
                      {review?.file_name?.toLowerCase().endsWith('.xlsx') && 'Excel cell comments (hover to view)'}
                      {review?.file_name?.toLowerCase().endsWith('.pdf') && 'PDF annotations (sticky notes)'}
                      {review?.file_name?.toLowerCase().endsWith('.txt') && 'Comments as text markers [COMMENT: ...]'}
                      {!review?.file_name && 'Comments included in document'}
                    </p>
                  </div>
                </label>
                
                {/* Track Changes - Best for DOCX/XLSX */}
                <label 
                  className={`flex items-start gap-3 p-4 rounded-xl cursor-pointer border-2 transition-all ${exportType === 'tracked' ? 'border-blue-500 bg-blue-50' : 'border-transparent bg-gray-50 hover:bg-gray-100'}`}
                >
                  <input 
                    type="radio" 
                    name="exportType" 
                    value="tracked"
                    checked={exportType === 'tracked'}
                    onChange={(e) => setExportType(e.target.value)}
                    className="mt-1 accent-blue-500"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">📝 Track Changes</h4>
                    <p className="text-sm text-gray-500">
                      {(review?.file_name?.toLowerCase().endsWith('.docx') || review?.file_name?.toLowerCase().endsWith('.xlsx')) 
                        ? 'Deletions (strikethrough) and insertions (underline)' 
                        : 'Shows old and new values with markers'}
                    </p>
                  </div>
                </label>
                
                {/* Comparison */}
                <label 
                  className={`flex items-start gap-3 p-4 rounded-xl cursor-pointer border-2 transition-all ${exportType === 'comparison' ? 'border-blue-500 bg-blue-50' : 'border-transparent bg-gray-50 hover:bg-gray-100'}`}
                >
                  <input 
                    type="radio" 
                    name="exportType" 
                    value="comparison"
                    checked={exportType === 'comparison'}
                    onChange={(e) => setExportType(e.target.value)}
                    className="mt-1 accent-blue-500"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">📋 Comparison</h4>
                    <p className="text-sm text-gray-500">Side-by-side before/after view</p>
                  </div>
                </label>
              </div>
              
              <div className="flex gap-3">
                <button 
                  onClick={() => setShowExportModal(false)}
                  className="flex-1 py-3 text-gray-600 bg-gray-100 rounded-xl font-semibold hover:bg-gray-200 transition-all"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleExport}
                  disabled={exporting}
                  className="flex-1 py-3 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {exporting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download size={18} />
                      Download {review?.file_name?.split('.').pop()?.toUpperCase() || 'File'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* SUPER REVIEW UPLOAD */}
        {reviewMode === 'super-review' && view === 'upload' && (
          <div className="max-w-3xl mx-auto space-y-6">
            {srHasBothFiles === null && (
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                    <Sparkles size={20} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Super Review</h2>
                    <p className="text-sm text-gray-500">Let us set up your review</p>
                  </div>
                </div>
                <h3 className="text-base font-semibold text-gray-800 mb-2">Do you have both source and translated documents?</h3>
                <p className="text-sm text-gray-500 mb-6">If you have both, we deliver more accurate results. If you only have the translated document, we can still process it.</p>
                <div className="grid grid-cols-2 gap-4">
                  <button onClick={() => setSrHasBothFiles(true)} className="p-5 rounded-xl border-2 border-gray-200 hover:border-emerald-400 hover:bg-emerald-50 transition-all text-left group">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center mb-3"><CheckCircle size={20} className="text-emerald-600" /></div>
                    <h4 className="font-semibold text-gray-900 group-hover:text-emerald-700">Yes, I have both</h4>
                    <p className="text-xs text-gray-500 mt-1">Source + translated — best results</p>
                  </button>
                  <button onClick={() => setSrHasBothFiles(false)} className="p-5 rounded-xl border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all text-left group">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center mb-3"><FileText size={20} className="text-blue-600" /></div>
                    <h4 className="font-semibold text-gray-900 group-hover:text-blue-700">No, only translated</h4>
                    <p className="text-xs text-gray-500 mt-1">Translation only — still works well</p>
                  </button>
                </div>
              </div>
            )}

            {srHasBothFiles !== null && (
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center"><Sparkles size={20} className="text-white" /></div>
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">Super Review</h2>
                      <p className="text-sm text-gray-500">{srHasBothFiles ? 'Source + translated document' : 'Translated document only'}</p>
                    </div>
                  </div>
                  <button onClick={() => setSrHasBothFiles(null)} className="text-sm text-gray-400 hover:text-gray-600">← Change</button>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5 text-sm text-amber-700">
                  <strong>Note:</strong> Add reviewer comments inside the document using Word or Google Docs comment feature before uploading.
                </div>

                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Target Language <span className="text-gray-400 font-normal">(language being reviewed)</span></label>
                  <select value={srTargetLang} onChange={e => setSrTargetLang(e.target.value)} className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-blue-400 bg-white">
                    {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>



                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Your Name <span className="text-gray-400 font-normal">(appears on review replies in document)</span></label>
                  <input
                    type="text"
                    value={srReviewerName}
                    onChange={e => setSrReviewerName(e.target.value)}
                    placeholder="e.g. Priya Sharma"
                    className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-blue-400"
                  />
                </div>

                {srHasBothFiles && (
                  <div className="mb-5">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Source Document <span className="text-gray-400">(Original Language)</span></label>
                    <div className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${srSourceFile ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50'}`} onClick={() => document.getElementById('sr-source-file').click()}>
                      <input id="sr-source-file" type="file" accept=".docx,.doc,.txt" hidden onChange={e => { setSrSourceFile(e.target.files[0]); handleSrFileRead(e.target.files[0], setSrSourceText); }} />
                      {srSourceFile ? <><FileCheck size={32} className="mx-auto mb-2 text-green-500" /><p className="text-sm font-medium text-gray-700">{srSourceFile.name}</p><span onClick={e=>{e.stopPropagation();setSrSourceFile(null);setSrSourceText('');}} className="text-xs text-gray-400 hover:text-red-500 cursor-pointer mt-1 block">Remove</span></> : <><FileUp size={32} className="mx-auto mb-2 text-gray-400" /><p className="text-sm font-medium text-gray-700">Upload source document</p><p className="text-xs text-gray-400 mt-1">.docx, .doc, .txt</p></>}
                    </div>
                  </div>
                )}

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Translated Document <span className="text-emerald-600 text-xs font-medium ml-1">← with reviewer comments inside</span></label>
                  <div className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${srTranslatedFile ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50'}`} onClick={() => document.getElementById('sr-target-file').click()}>
                    <input id="sr-target-file" type="file" accept=".docx,.doc,.txt" hidden onChange={e => { setSrTranslatedFile(e.target.files[0]); handleSrFileRead(e.target.files[0], (text) => { setSrTranslatedText(text); setSrWordCount(text.trim().split(/\s+/).filter(Boolean).length); }); }} />
                    {srTranslatedFile ? <><FileCheck size={32} className="mx-auto mb-2 text-green-500" /><p className="text-sm font-medium text-gray-700">{srTranslatedFile.name}</p><span onClick={e=>{e.stopPropagation();setSrTranslatedFile(null);setSrTranslatedText('');}} className="text-xs text-gray-400 hover:text-red-500 cursor-pointer mt-1 block">Remove</span></> : <><FileUp size={32} className="mx-auto mb-2 text-gray-400" /><p className="text-sm font-medium text-gray-700">Upload translated document</p><p className="text-xs text-gray-400 mt-1">.docx with reviewer comments (recommended), .doc, .txt</p></>}
                  </div>
                </div>

                {srTranslatedFile && srWordCount > 0 && (
                  <div className="mb-4">
                    <CostEstimateApple
                      feature="super_review"
                      targetWords={Math.ceil(srWordCount * 0.5)}
                    />
                    <p className="text-xs text-gray-400 mt-1 px-1">* Estimate based on target text. Exact cost calculated on processing.</p>
                  </div>
                )}
                <button
                  onClick={handleSuperReview}
                  disabled={srLoading || !srTranslatedFile || (srHasBothFiles && !srSourceFile)}
                  className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold flex items-center justify-center gap-2 transition-colors"
                >
                  {srLoading ? <><Loader2 size={18} className="animate-spin" /> Reading comments & processing...</> : <><Sparkles size={18} /> Run Super Review</>}
                </button>
                {srLoading && srCommentTicker.total > 0 && (
                  <div style={{ marginTop: 16, padding: '14px 16px', background: isDarkMode ? '#052e16' : '#f0fdf4', border: `1px solid ${isDarkMode ? '#166534' : '#bbf7d0'}`, borderRadius: 12, animation: 'sr-ticker-in 0.25s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                    <style>{`@keyframes sr-ticker-in { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }`}</style>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: isDarkMode ? '#4ade80' : '#166534' }}>Processing comments</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: isDarkMode ? '#4ade80' : '#15803d' }}>{srCommentTicker.processed} / {srCommentTicker.total}</span>
                    </div>
                    <div style={{ height: 4, background: isDarkMode ? '#14532d' : '#dcfce7', borderRadius: 4, overflow: 'hidden', marginBottom: 10 }}>
                      <div style={{ height: '100%', width: `${srCommentTicker.total > 0 ? (srCommentTicker.processed / srCommentTicker.total) * 100 : 0}%`, background: isDarkMode ? '#4ade80' : '#16a34a', borderRadius: 4, transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }} />
                    </div>
                    <div style={{ display: 'flex', gap: 16 }}>
                      <span style={{ fontSize: 12, color: isDarkMode ? '#4ade80' : '#15803d', fontWeight: 600 }}>&#10003; Accepted: {srCommentTicker.accepted}</span>
                      <span style={{ fontSize: 12, color: isDarkMode ? '#f87171' : '#dc2626', fontWeight: 600 }}>&#10005; Rejected: {srCommentTicker.rejected}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* SUPER REVIEW RESULTS */}
        {reviewMode === 'super-review' && view === 'results' && srResults && (
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="flex border-b border-gray-100">
                {[
                  { id: 'clean', label: 'Clean Final', icon: '✅' },
                  { id: 'highlighted', label: 'Highlighted Changes', icon: '🟡' },
                  { id: 'resolution', label: 'Review Resolution', icon: '📋' },
                ].map(tab => (
                  <button key={tab.id} onClick={() => setSrActiveTab(tab.id)} className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${srActiveTab === tab.id ? 'bg-emerald-50 text-emerald-700 border-b-2 border-emerald-500' : 'text-gray-500 hover:text-gray-700'}`}>
                    {tab.icon} {tab.label}
                  </button>
                ))}
              </div>
              <div className="p-6">
                {srActiveTab === 'clean' && (
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="font-semibold text-gray-900">Production-Ready Translation</h3>
                      <button onClick={() => navigator.clipboard.writeText(srResults.clean)} className="text-xs px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600">Copy</button>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed border border-gray-200">{srResults.clean}</div>
                    {srResults.clean_docx_b64 && (
                      <button onClick={() => downloadSrDocx(srResults.clean_docx_b64, `Comments-Audit-Completed-${srResults.original_filename || 'document.docx'}`)} className="mt-3 flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium">
                        <Download size={14} /> Download Clean DOCX
                      </button>
                    )}
                  </div>
                )}
                {srActiveTab === 'highlighted' && (
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="font-semibold text-gray-900">Changes Highlighted</h3>
                      <span className="text-xs text-amber-600 font-medium">🟡 Yellow = modified text</span>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-800 leading-relaxed border border-gray-200" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(srResults.highlighted) }} />
                    {srResults.highlighted_docx_b64 && (
                      <button onClick={() => downloadSrDocx(srResults.highlighted_docx_b64, `super-review-highlighted-${srResults.original_filename || 'document.docx'}`)} className="mt-3 flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium">
                        <Download size={14} /> Download Highlighted DOCX
                      </button>
                    )}
                  </div>
                )}
                {srActiveTab === 'resolution' && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4">Review Resolution Report</h3>
                    <div className="space-y-4">
                      {srResults.resolutions?.map((item, i) => (
                        <div key={i} className={`rounded-xl p-4 border ${item.status === 'RESOLVED' ? 'bg-green-50 border-green-200' : item.status === 'REJECTED' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-bold text-gray-500">COMMENT {i + 1}</span>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${item.status === 'RESOLVED' ? 'bg-green-100 text-green-700' : item.status === 'REJECTED' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{item.status}</span>
                          </div>
                          <div className="space-y-1.5 text-sm">
                            <p><span className="font-medium text-gray-600">Reviewer comment:</span> {item.comment}</p>
                            {item.old_target && <p><span className="font-medium text-gray-600">Original:</span> <span className="text-red-600 line-through">{item.old_target}</span></p>}
                            {item.new_target && item.status !== 'REJECTED' && <p><span className="font-medium text-gray-600">Updated:</span> <span className="text-green-700 font-medium">{item.new_target}</span></p>}
                            <p><span className="font-medium text-gray-600">Note:</span> {item.note}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    {srResults.resolution_docx_b64 && (
                      <button onClick={() => downloadSrDocx(srResults.resolution_docx_b64, `super-review-resolution-${srResults.original_filename || 'document.docx'}`)} className="mt-4 flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium">
                        <Download size={14} /> Download Resolution Report DOCX
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
            {srResults.stats && (
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">Summary</h3>
                  {srResults.words_charged && <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-lg">Words charged: <strong>{srResults.words_charged}</strong></span>}
                </div>
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div><div className="text-2xl font-bold text-gray-900">{srResults.stats.total}</div><div className="text-xs text-gray-500">Total Comments</div></div>
                  <div><div className="text-2xl font-bold text-green-600">{srResults.stats.resolved}</div><div className="text-xs text-gray-500">Resolved</div></div>
                  <div><div className="text-2xl font-bold text-amber-600">{srResults.stats.modified}</div><div className="text-xs text-gray-500">Modified</div></div>
                  <div><div className="text-2xl font-bold text-red-600">{srResults.stats.rejected}</div><div className="text-xs text-gray-500">Rejected</div></div>
                </div>
              </div>
            )}
            <button onClick={() => { setSrResults(null); setView('upload'); setSrActiveTab('clean'); setSrHasBothFiles(null); setSrSourceText(''); setSrTranslatedText(''); setSrReviewerComments(''); setSrReviewerName(''); }} className="w-full py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium">← New Super Review</button>
          </div>
        )}
      </div>
      {showExportFeedback && (
        <ExportFeedbackModal
          feature={exportFeedbackFeature}
          onClose={() => setShowExportFeedback(false)}
        />
      )}
      <ReferralToast
        toast={referralToast}
        onDismiss={() => setReferralToast(null)}
      />
    </MainLayoutApple>
  );
};

export default ReviewPageApple;
